/**
 * ProfileSwitcher
 * Toggle between personal and professional profile views
 */

import { Card } from '@/shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils'
import { Briefcase, User } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

export type ProfileType = 'personal' | 'professional'

interface ProfileSwitcherProps {
  activeProfile: ProfileType
  onSwitch: (profile: ProfileType) => void
  isAccountValidated: boolean | undefined// Whether user has professional profile
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function ProfileSwitcher({
  activeProfile,
  onSwitch,
  isAccountValidated = false,
  className,
  ...props
}: ProfileSwitcherProps & React.HTMLAttributes<HTMLDivElement>) {

  return (
    <Card className="flex flex-col px-4 pt-2 pb-4 mb-6" {...props}>

      <div className="w-full px-2 mb-2">
        <span className='text-muted-foreground'>Perfiles</span>
      </div>

      <div className={cn('flex gap-4 px-2  rounded-lg', className)}>

        {/* Personal Profile Button */}
        <Button
          type="button"
          variant={activeProfile === 'personal' ? 'default' : 'outline'}
          className={cn(
            'flex-1 transition-all',
            activeProfile === 'personal' && 'shadow-sm'
          )}
          onClick={() => onSwitch('personal')}
        >
          <User className="h-4 w-4 mr-2" />
          <span>Personal</span>
        </Button>

        {/* Professional Profile Button */}
        <Button
          type="button"
          disabled={!isAccountValidated}
          variant={activeProfile === 'professional' ? 'default' : 'outline'}
          className={cn(
            'flex-1 transition-all',
            activeProfile === 'professional' && 'shadow-sm'
          )}
          onClick={() => onSwitch('professional')}
          data-tour="professional-tab"
        >
          <Briefcase className="h-4 w-4 mr-2" />
          <span>
            <span className="font-semibold text-ring">Pro</span>
            <span className="font-semibold">Fessional</span>
          </span>
        </Button>
      </div>
    </Card>
  )
}
